import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import { ContentMaps } from '../../../../api/models/BibleStudy';
import { HorizontalContent } from '../../../core/HorizontalContent';
import { RhButton, RhButtonMode } from '../../../core/RhButton';
import { RhInput, RhInputMode } from '../../../core/RhInput';
import { RhModal } from '../../../core/RhModal';
import { SizedBox } from '../../../core/SizedBox';
import { colors, H3, H4, H5 } from '../../../core/Styles';

interface GuidedContentCreationModalProps {
  initial?: ContentMaps;
  isOpen: boolean;
  isLoading?: boolean;
  onCancel?: () => void;
  onSubmit?: (value: ContentMaps) => void;
}

type FormState = {
  /** Number of weeks present in a study program */
  weeks: number;
  /** If each week has the same number of days */
  sameDays: boolean;
  /** Number of days in each week */
  days: number;
  /** This is a map or a library. Example: { week_1: 1, week_2: 2 } */
  daysPerWeek: { [key: string]: number };
};

type FormStateErrors = {
  [key in keyof FormState]?: string;
};

const initialState: FormState = {
  weeks: 1,
  sameDays: false,
  days: 0,
  daysPerWeek: {},
};

const validateFormState = (formState: FormState) => {
  const errors: FormStateErrors = {};

  if (!formState.weeks) {
    errors.weeks = 'You have to put in the number of weeks';
  }
  if (formState.sameDays && !formState.days) {
    errors.days = 'You have to put in the number of days!';
  }
  if (!formState.sameDays && _.isEmpty(formState.daysPerWeek)) {
    errors.daysPerWeek = 'You have to put in days per week!';
  }
  if (!formState.sameDays && !_.isEmpty(formState.daysPerWeek)) {
    for (let i = 1; i <= formState.weeks; i++) {
      if (!formState.daysPerWeek[`week_${i}`]) {
        errors.daysPerWeek = 'You missed a week!';
        break;
      }
    }
  }

  const hasErrors = !_.isEmpty(errors);
  return { hasErrors, errors };
};

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

export const GuidedContentCreationModal = (props: GuidedContentCreationModalProps) => {
  const [formState, setFormState] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<FormStateErrors>({});

  useEffect(() => {
    if (!props.initial?.length) return;

    const newFormState: FormState = { ...initialState };

    newFormState.weeks = props.initial.length;
    const isSameDays = props.initial.every(item => item.days === props.initial?.[0].days);
    if (isSameDays) {
      newFormState.sameDays = true;
      newFormState.days = props.initial[0].days;
    }
    props.initial.forEach(item => {
      newFormState.daysPerWeek[`week_${item.week}`] = item.days;
    });

    setFormState(newFormState);
  }, [props.initial]);

  return (
    <RhModal isOpen={props.isOpen} shouldCloseOnEsc={false} shouldCloseOnOverlayClick={false}>
      <div>
        <HorizontalContent
          left={<H3>Guided Content Creation</H3>}
          right={props.isLoading && <CircleSpinner key="loader" size={15} color={colors.primary} />}
        />
        <SizedBox height={24} />
        <div>
          <H5>How many weeks are in this study?</H5>
          <SizedBox height={6} />
          <RhInput
            mode={RhInputMode.subtle}
            placeholder="e.g. - 5"
            type="number"
            min={1}
            value={formState.weeks}
            onChange={e => setFormState(previousState => ({ ...previousState, weeks: parseInt(e.target.value) }))}
          />
          {!!errors.weeks && <H4 style={{ color: 'red', marginTop: 4 }}>{errors.weeks}</H4>}
        </div>
      </div>
      {!!formState.weeks && (
        <div style={{ maxHeight: '50vh', overflowY: 'scroll', marginRight: -30, paddingRight: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={formState.sameDays}
              onChange={e => setFormState(prevState => ({ ...prevState, sameDays: e.target.checked }))}
            />
            <SizedBox width={6} />
            <H4>Each week has the same days</H4>
          </div>
          <SizedBox height={16} />
          {formState.sameDays ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <H5>Specify days per week:</H5>
                <SizedBox width={6} />
                <RhInput
                  mode={RhInputMode.subtle}
                  placeholder="e.g. - 5"
                  type="number"
                  min={1}
                  value={formState.days}
                  onChange={e => setFormState(previousState => ({ ...previousState, days: parseInt(e.target.value) }))}
                />
              </div>
              {!!errors.days && <H4 style={{ color: 'red', marginTop: 4 }}>{errors.days}</H4>}
            </div>
          ) : (
            [...new Array(formState.weeks)].map((_, i) => {
              const key = `week_${i + 1}`;
              return (
                <div
                  key={key}
                  style={{ display: 'flex', alignItems: 'center', marginBottom: i < formState.weeks ? 12 : 0 }}>
                  <H5 style={{ flex: 1 }}>
                    How many days are in <strong>Week {i + 1}</strong>?
                  </H5>
                  <SizedBox width={16} />
                  <RhInput
                    mode={RhInputMode.subtle}
                    placeholder={`Days of Week ${i + 1}`}
                    type="number"
                    min={1}
                    value={formState.daysPerWeek[key]}
                    onChange={e => {
                      setFormState(prevState => {
                        const newDaysPerWeek = { ...prevState.daysPerWeek };
                        newDaysPerWeek[key] = parseInt(e.target.value);
                        return { ...prevState, daysPerWeek: newDaysPerWeek };
                      });
                    }}
                  />
                </div>
              );
            })
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
        {!!errors.daysPerWeek ? <H4 style={{ color: 'red', marginTop: 4 }}>{errors.daysPerWeek}</H4> : <div />}
        <div style={{ display: 'flex' }}>
          <RhButton
            mode={RhButtonMode.secondary}
            title="Cancel"
            onClick={() => {
              // Resetform
              setFormState(initialState);
              // Reset errors
              setErrors({});
              // Call callback
              props.onCancel?.();
            }}
          />
          <SizedBox width={16} />
          <RhButton
            mode={RhButtonMode.primary}
            title="OK"
            onClick={() => {
              // Validate form
              const { hasErrors, errors } = validateFormState(formState);
              if (hasErrors) {
                return setErrors(errors);
              }

              // Submit form
              const contentMaps: ContentMaps = [];
              for (let week = 1; week <= formState.weeks; week++) {
                if (formState.sameDays) {
                  contentMaps.push({ week, days: formState.days });
                } else {
                  contentMaps.push({ week, days: formState.daysPerWeek[`week_${week}`] });
                }
              }

              // Resetform
              setFormState(initialState);
              // Reset errors
              setErrors({});

              // Call callback
              props.onSubmit?.(contentMaps);
            }}
          />
        </div>
      </div>
    </RhModal>
  );
};
